/**
 * Preflop ranges, built by slicing the hand ordering and reshaping it by position.
 *
 * A pure percentile cut is the wrong shape: an early seat should not be playing the
 * same 20% as a late seat, it should be playing a DIFFERENT 20% — fewer suited aces
 * and small pairs, which need position and a multiway pot to be worth anything, and
 * more high cards, which do not. So position adjusts the score before the cut rather
 * than only adjusting the width.
 *
 * These are heuristics. The solved charts take precedence wherever they apply, and
 * nothing here is presented to a customer as a solve.
 */
import { comboCount } from "../../pipeline/solve/evaluator";
import { Range } from "../range";
import { HAND_ORDER, handRank } from "./ranking";
import type { BotStyle } from "./profile";

/** Seats named from the button backwards, which is how position actually works. */
export type Position = "BTN" | "SB" | "BB" | "CO" | "HJ" | "LJ" | "UTG1" | "UTG" | "UTG2";

/**
 * How many seats will act after this one preflop, counting the blinds.
 * The only positional fact that matters: everything else is a name for it.
 */
export function seatsBehind(seatsFromButton: number): number { return seatsFromButton; }

const R = "23456789TJQKA";

interface Shape { hi: number; lo: number; suited: boolean; pair: boolean; gap: number }

const SHAPE: ReadonlyMap<string, Shape> = new Map(HAND_ORDER.map((code) => {
  const hi = R.indexOf(code[0]!), lo = R.indexOf(code[1]!);
  const pair = hi === lo;
  return [code, { hi, lo, suited: code.endsWith("s"), pair, gap: pair ? -1 : hi - lo - 1 }];
}));

/**
 * Positional reshaping, in units of ordering places. Positive moves a hand LATER in
 * the ordering (worse) — which is what happens to speculative hands out of position.
 *
 * `earliness` is 0 on the button and 1 in the first seat to act.
 */
function positionalShift(code: string, earliness: number, awareness: number): number {
  const s = SHAPE.get(code)!;
  let shift = 0;
  // Speculative hands need position and callers; from early seats they get neither.
  if (s.pair && s.hi < 7) shift += 30;
  if (s.suited && !s.pair && s.hi === 12 && s.lo <= 8) shift += 22;   // low suited aces
  if (s.suited && !s.pair && s.gap >= 0 && s.hi <= 9) shift += 24;    // suited connectors
  if (s.suited && !s.pair && s.hi <= 11 && s.lo <= 6) shift += 14;    // suited one-gappers
  /**
   * High cards hold their value out of position better than anything else does, and a
   * big pair is the one speculative-looking hand that does not need a flop to win.
   *
   * The offsuit adjustment is graded by the LOW card, because that is what decides
   * whether the hand can be played after the flop. AJo out of position is a hand;
   * A5o out of position is a way to make top pair with the worst kicker and pay off.
   * Without the penalty on the bottom half, holding the width fixed pulled offsuit
   * ace-rag INTO early position as the speculative hands were pushed out.
   */
  if (!s.suited && !s.pair) shift += s.lo >= 9 ? -16 : s.lo === 8 ? 0 : 20;
  if (s.pair && s.hi >= 9) shift -= 10;
  return shift * earliness * awareness;
}

export interface RangeSpec {
  /** Share of all hands, 0..100. */
  percent: number;
  /** 0 on the button, 1 in the earliest seat. */
  earliness: number;
  style: Pick<BotStyle, "looseness" | "positionAwareness">;
}

/**
 * The set of hand codes a seat plays, as a sorted list of codes.
 *
 * Combo-weighted, because "22%" means 22% of the 1,326 combinations and not 22% of
 * the 169 names — pairs are six combos and offsuit hands are twelve, so counting names
 * would make a range of pairs four times wider than it looks.
 */
export function rangeCodes(spec: RangeSpec): string[] {
  const target = Math.max(0, Math.min(100, spec.percent * spec.style.looseness));
  const scored = HAND_ORDER.map((code) => ({
    code,
    key: handRank(code) + positionalShift(code, spec.earliness, spec.style.positionAwareness),
  })).sort((a, b) => a.key - b.key);

  const limit = (target / 100) * 1326;
  /**
   * At least one hand, always. A range of nothing is not a strategy the engine can
   * act on — a seat has to be able to do something with aces — so the floor is the
   * top of the ordering rather than an empty list.
   */
  const out: string[] = [];
  let cum = 0;
  for (const { code } of scored) {
    const n = comboCount(code);
    if (cum + n > limit && out.length > 0) break;
    out.push(code);
    cum += n;
  }
  return out;
}

export function rangeOf(spec: RangeSpec): Range {
  const codes = rangeCodes(spec);
  const w: Record<string, number> = {};
  for (const c of codes) w[c] = 1;
  return Range.fromCodes(w);
}

/**
 * Baseline opening widths by how many seats are still to act.
 *
 * Calibrated to the shapes published range charts agree on — an early seat at a full
 * table opens around a fifth of hands, the button around half — rather than derived
 * from anything. They move with `looseness`, which is what makes a maniac a maniac.
 */
export function openPercent(behind: number): number {
  const table = [82, 55, 42, 33, 27, 23, 20, 18, 17];
  return table[Math.max(0, Math.min(table.length - 1, behind))]!;
}

/** Small blind opening into one player is a different, much wider spot than a raise into five. */
export function sbOpenPercent(): number { return 48; }

/**
 * Continuing against a single raise, split into the three things a hand can do.
 *
 * The 3-bet range is deliberately NOT the top of the calling range: it is the top plus
 * a slice from further down, because a 3-betting range made only of premiums is
 * trivially readable and every opponent folds correctly against it. The bluff slice is
 * taken from suited hands with blockers, which is where it belongs.
 */
export interface DefenceSpec {
  /** How wide the raiser is, 0..100 — from the reads, or the baseline. */
  vsPercent: number;
  /** Position of the defender relative to the raiser: true when acting last. */
  inPosition: boolean;
  style: BotStyle;
  /** Effective stack in big blinds, which decides whether 3-betting is even sane. */
  effectiveBb: number;
}

export interface Defence {
  threeBet: string[];
  call: string[];
  /** The bluff half of the 3-bet range, for a caller that wants to know. */
  threeBetBluffs: string[];
}

export function defenceAgainstRaise(spec: DefenceSpec): Defence {
  const { style } = spec;
  // A tighter raiser means a tighter continuing range; a wider one means a wider one.
  const looseness = style.looseness;
  const openness = Math.max(0.5, Math.min(2, spec.vsPercent / 22));

  const valueWidth = 3.2 * openness * looseness * (0.75 + style.aggression * 0.5);
  const bluffWidth = valueWidth * 0.9 * style.bluffiness;
  const callWidth = (spec.inPosition ? 16 : 9) * openness * looseness
    * (0.7 + style.stickiness * 0.6);

  const ordered = HAND_ORDER;
  const value = takePercent(ordered, valueWidth);
  const taken = new Set(value);

  /**
   * Bluffs: suited hands just outside the calling range, preferring ones that block
   * the hands that continue. A short stack has no room for this — with twenty blinds
   * behind, a 3-bet bluff is a shove, and a shove with a bluff is a different decision
   * the push/fold charts already answer.
   */
  const bluffs: string[] = [];
  if (spec.effectiveBb >= 25) {
    for (const code of ordered) {
      if (taken.has(code)) continue;
      const s = SHAPE.get(code)!;
      // Rank index 11 is a king and 12 an ace. The earlier `>= 10` was a queen, which
      // is not a blocker to the hands that continue against a three-bet.
      const blocks = s.hi >= 11;
      if (!s.suited || s.pair) continue;
      if (!blocks && s.gap > 1) continue;
      // Checked BEFORE pushing: pushing first meant a profile with bluffiness zero
      // still three-bet one hand as a bluff, which is not "never".
      if (combosOfList([...bluffs, code]) > (bluffWidth / 100) * 1326) break;
      bluffs.push(code);
    }
  }
  for (const b of bluffs) taken.add(b);

  const call: string[] = [];
  for (const code of ordered) {
    if (taken.has(code)) continue;
    call.push(code);
    if (combosOfList(call) >= (callWidth / 100) * 1326) break;
  }

  return { threeBet: [...value, ...bluffs], call, threeBetBluffs: bluffs };
}

function takePercent(ordered: readonly string[], percent: number): string[] {
  const limit = (Math.max(0, percent) / 100) * 1326;
  const out: string[] = [];
  let cum = 0;
  for (const code of ordered) {
    const n = comboCount(code);
    if (cum + n > limit && out.length > 0) break;
    out.push(code);
    cum += n;
  }
  return out;
}

function combosOfList(codes: readonly string[]): number {
  let t = 0;
  for (const c of codes) t += comboCount(c);
  return t;
}

/** Percent of all combinations a list of codes covers. */
export function percentOf(codes: readonly string[]): number {
  return (combosOfList(codes) / 1326) * 100;
}
